import { Extension, type CommandProps } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, TextSelection, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { ORIGEM_IA } from '../versoes';

/** Trechos de texto do documento com a posição de cada caractere. */
function blocosDeTexto(doc: PMNode): { texto: string; pos: number[] }[] {
  const blocos: { texto: string; pos: number[] }[] = [];
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    let texto = '';
    const mapa: number[] = [];
    node.forEach((filho, desloc) => {
      if (!filho.isText || !filho.text) { texto += '⁣'; mapa.push(pos + 1 + desloc); return; }
      for (let i = 0; i < filho.text.length; i++) { texto += filho.text[i]; mapa.push(pos + 1 + desloc + i); }
    });
    blocos.push({ texto, pos: mapa });
    return false;
  });
  return blocos;
}

const LETRA = 'A-Za-zÀ-ÖØ-öø-ÿ';

/* ===================== Localizar e substituir ===================== */

export interface EstadoBusca {
  termo: string;
  diferenciarCaixa: boolean;
  palavraInteira: boolean;
  achados: { from: number; to: number }[];
  atual: number;
}

export const CHAVE_BUSCA = new PluginKey<EstadoBusca>('busca');

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    busca: {
      buscar: (termo: string, opcoes?: { diferenciarCaixa?: boolean; palavraInteira?: boolean }) => ReturnType;
      irParaAchado: (passo: 1 | -1) => ReturnType;
      substituirAtual: (novo: string) => ReturnType;
      substituirTodos: (novo: string) => ReturnType;
      limparBusca: () => ReturnType;
    };
    ortografia: {
      /** Liga/desliga o dicionário embutido. */
      definirOrtografia: (ativa: boolean) => ReturnType;
      /** Refaz a verificação (depois de trocar o dicionário). */
      reverificar: () => ReturnType;
    };
    sugestoesIA: {
      definirSugestoes: (lista: SugestaoIA[]) => ReturnType;
      resolverSugestao: (id: string, aceitar: boolean) => ReturnType;
      /** Todas as pendentes numa transação só: um Ctrl+Z desfaz o lote inteiro. */
      aceitarTodasSugestoes: () => ReturnType;
      focarSugestao: (id: string | null) => ReturnType;
    };
  }
}

function procurar(doc: PMNode, termo: string, caixa: boolean, inteira: boolean) {
  const achados: { from: number; to: number }[] = [];
  if (!termo) return achados;
  const esc = termo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(inteira ? `(^|[^${LETRA}0-9])(${esc})(?=$|[^${LETRA}0-9])` : `()(${esc})`, caixa ? 'g' : 'gi');
  for (const b of blocosDeTexto(doc)) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(b.texto))) {
      const ini = m.index + m[1].length;
      achados.push({ from: b.pos[ini], to: b.pos[ini + m[2].length - 1] + 1 });
      if (re.lastIndex === m.index) re.lastIndex++;
    }
  }
  return achados;
}

export const Busca = Extension.create({
  name: 'busca',
  addProseMirrorPlugins() {
    return [
      new Plugin<EstadoBusca>({
        key: CHAVE_BUSCA,
        state: {
          init: () => ({ termo: '', diferenciarCaixa: false, palavraInteira: true, achados: [], atual: -1 }),
          apply: (tr, val) => {
            const meta = tr.getMeta(CHAVE_BUSCA) as Partial<EstadoBusca> | undefined;
            let prox = meta ? { ...val, ...meta } : val;
            if (meta?.termo !== undefined || meta?.diferenciarCaixa !== undefined || meta?.palavraInteira !== undefined || (tr.docChanged && prox.termo)) {
              const achados = procurar(tr.doc, prox.termo, prox.diferenciarCaixa, prox.palavraInteira);
              prox = { ...prox, achados, atual: achados.length ? Math.min(Math.max(prox.atual, 0), achados.length - 1) : -1 };
            }
            return prox;
          },
        },
        props: {
          decorations: (state) => {
            const b = CHAVE_BUSCA.getState(state);
            if (!b?.achados.length) return null;
            return DecorationSet.create(
              state.doc,
              b.achados.map((a, i) => Decoration.inline(a.from, a.to, { class: i === b.atual ? 'cm-ef-achado cm-ef-achado-atual' : 'cm-ef-achado' })),
            );
          },
        },
      }),
    ];
  },
  addCommands() {
    const mostrar = (tr: Transaction, a?: { from: number; to: number }) => {
      if (a) tr.setSelection(TextSelection.create(tr.doc, a.from, a.to)).scrollIntoView();
      return tr;
    };
    return {
      buscar:
        (termo, opcoes = {}) =>
        ({ tr, dispatch }: CommandProps) => {
          if (dispatch) dispatch(tr.setMeta(CHAVE_BUSCA, { termo, atual: 0, ...opcoes }));
          return true;
        },
      irParaAchado:
        (passo) =>
        ({ state, tr, dispatch }: CommandProps) => {
          const b = CHAVE_BUSCA.getState(state);
          if (!b?.achados.length) return false;
          const atual = (b.atual + passo + b.achados.length) % b.achados.length;
          if (dispatch) dispatch(mostrar(tr.setMeta(CHAVE_BUSCA, { atual }), b.achados[atual]));
          return true;
        },
      substituirAtual:
        (novo) =>
        ({ state, tr, dispatch }: CommandProps) => {
          const b = CHAVE_BUSCA.getState(state);
          const a = b?.achados[b.atual];
          if (!a) return false;
          if (dispatch) dispatch(tr.insertText(novo, a.from, a.to));
          return true;
        },
      substituirTodos:
        (novo) =>
        ({ state, tr, dispatch }: CommandProps) => {
          const b = CHAVE_BUSCA.getState(state);
          if (!b?.achados.length) return false;
          // Do fim para o começo: substituir não desloca os achados de antes.
          [...b.achados].reverse().forEach((a) => tr.insertText(novo, a.from, a.to));
          if (dispatch) dispatch(tr);
          return true;
        },
      limparBusca:
        () =>
        ({ tr, dispatch }: CommandProps) => {
          if (dispatch) dispatch(tr.setMeta(CHAVE_BUSCA, { termo: '', achados: [], atual: -1 }));
          return true;
        },
    };
  },
});

/* ===================== Ortografia ===================== */

/**
 * Quem verifica as palavras. O editor não traz dicionário: recebe um
 * provedor (`criarOrtografiaPtBr` monta o pt-BR com nspell + VERO) e soma o
 * dicionário pessoal de quem escreve.
 */
export interface ProvedorOrtografia {
  correta: (palavra: string) => boolean;
  sugestoes: (palavra: string) => string[];
}

export interface OpcoesOrtografia {
  /** Lido a cada verificação: o provedor pode chegar depois (carregado sob demanda). */
  provedor: () => ProvedorOrtografia | null;
  /** Palavras que a pessoa acrescentou (nomes de partes, termos do escritório). */
  dicionarioPessoal: () => string[];
}

export const CHAVE_ORTO = new PluginKey<{ ativa: boolean; decoracoes: DecorationSet }>('ortografia');

function verificar(doc: PMNode, op: OpcoesOrtografia): DecorationSet {
  const provedor = op.provedor();
  if (!provedor) return DecorationSet.empty;
  const pessoal = new Set(op.dicionarioPessoal().map((p) => p.toLocaleLowerCase('pt-BR')));
  const decs: Decoration[] = [];
  const re = new RegExp(`[${LETRA}]+(?:-[${LETRA}]+)*`, 'g');
  for (const b of blocosDeTexto(doc)) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(b.texto))) {
      const p = m[0];
      if (p.length < 2 || /^[A-ZÀ-Ý]{2,}$/.test(p) && p.length <= 5) continue; // siglas: STJ, CPF, OAB
      if (pessoal.has(p.toLocaleLowerCase('pt-BR')) || provedor.correta(p)) continue;
      decs.push(Decoration.inline(b.pos[m.index], b.pos[m.index + p.length - 1] + 1, { class: 'cm-ef-erro', 'data-palavra': p }));
    }
  }
  return DecorationSet.create(doc, decs);
}

export const Ortografia = Extension.create<OpcoesOrtografia>({
  name: 'ortografia',
  addOptions() {
    return { provedor: () => null, dicionarioPessoal: () => [] };
  },
  addProseMirrorPlugins() {
    const op = this.options;
    let espera: ReturnType<typeof setTimeout> | null = null;
    return [
      new Plugin({
        key: CHAVE_ORTO,
        state: {
          init: (_c, state) => ({ ativa: true, decoracoes: verificar(state.doc, op) }),
          apply: (tr, val, _v, novo) => {
            const meta = tr.getMeta(CHAVE_ORTO) as { ativa?: boolean; refazer?: boolean } | undefined;
            const ativa = meta?.ativa ?? val.ativa;
            if (!ativa) return { ativa, decoracoes: DecorationSet.empty };
            if (meta?.refazer || meta?.ativa) return { ativa, decoracoes: verificar(novo.doc, op) };
            return { ativa, decoracoes: val.decoracoes.map(tr.mapping, tr.doc) };
          },
        },
        props: { decorations: (state) => CHAVE_ORTO.getState(state)?.decoracoes ?? null },
        // Reverifica um instante depois de parar de digitar: verificar a cada
        // tecla numa peça de trinta páginas pesaria na digitação.
        view: () => ({
          update: (view, anterior) => {
            if (view.state.doc === anterior.doc) return;
            if (espera) clearTimeout(espera);
            espera = setTimeout(() => {
              if (!view.isDestroyed && CHAVE_ORTO.getState(view.state)?.ativa) view.dispatch(view.state.tr.setMeta(CHAVE_ORTO, { refazer: true }).setMeta('addToHistory', false));
            }, 600);
          },
          destroy: () => { if (espera) clearTimeout(espera); },
        }),
      }),
    ];
  },
  addCommands() {
    return {
      definirOrtografia:
        (ativa) =>
        ({ tr, dispatch }: CommandProps) => {
          if (dispatch) dispatch(tr.setMeta(CHAVE_ORTO, { ativa }).setMeta('addToHistory', false));
          return true;
        },
      reverificar:
        () =>
        ({ tr, dispatch }: CommandProps) => {
          if (dispatch) dispatch(tr.setMeta(CHAVE_ORTO, { refazer: true }).setMeta('addToHistory', false));
          return true;
        },
    };
  },
});

/* ===================== Sugestões da IA ===================== */

export interface SugestaoIA {
  id: string;
  /** Trecho como está no texto. */
  de: string;
  /** Como fica. */
  para: string;
  /** "Concordância", "Regência", "Estilo forense"… */
  tipo: string;
  /** Por quê, numa frase. */
  motivo: string;
}

/**
 * Revisão por IA fornecida pelo app: recebe o texto da peça e devolve as
 * sugestões. Sem ela, o botão "Revisar com IA" não aparece.
 */
export type Revisor = (texto: string) => Promise<SugestaoIA[]>;

/** `obsoleta`: o trecho mudou (reescrito, apagado, outra versão) depois da revisão. */
export type Resolucao = 'aceita' | 'recusada' | 'obsoleta';

interface SugestaoNoTexto extends SugestaoIA { from: number; to: number; resolvida?: Resolucao }

interface EstadoIA { lista: SugestaoNoTexto[]; foco: string | null }
export const CHAVE_IA = new PluginKey<EstadoIA>('sugestoesIA');

/**
 * Aplica a sugestão em ``tr`` se o trecho ainda é o que a IA leu (senão, false e
 * nada muda). Só o miolo que muda: o começo e o fim iguais ficam com a formatação
 * que têm (negrito, marca-texto) — trocar o trecho inteiro por um texto só levava
 * ou espalhava a marca do primeiro caractere.
 */
function aplicarSugestao(tr: Transaction, s: SugestaoNoTexto): boolean {
  if (s.to <= s.from || tr.doc.textBetween(s.from, s.to, '\n', '\u2063') !== s.de) return false;
  const { de, para } = s;
  let ini = 0;
  while (ini < de.length && ini < para.length && de[ini] === para[ini]) ini++;
  let fim = 0;
  while (fim < de.length - ini && fim < para.length - ini && de[de.length - 1 - fim] === para[para.length - 1 - fim]) fim++;
  const meio = para.slice(ini, para.length - fim);
  const a = s.from + ini;
  const b = s.to - fim;
  if (meio) tr.insertText(meio, a, b);
  else tr.delete(a, b);
  return true;
}

export const SugestoesIA = Extension.create({
  name: 'sugestoesIA',
  addProseMirrorPlugins() {
    return [
      new Plugin<EstadoIA>({
        key: CHAVE_IA,
        state: {
          init: (): EstadoIA => ({ lista: [], foco: null }),
          apply: (tr, val) => {
            const meta = tr.getMeta(CHAVE_IA) as { lista?: SugestaoNoTexto[]; foco?: string | null; resolvidas?: { id: string; como: Resolucao }[] } | undefined;
            // Trecho reescrito ou apagado por cima da sugestão: o intervalo colapsa
            // (ou se inverte) e ela deixa de valer — aceitá-la duplicaria o texto.
            let lista = meta?.lista ?? val.lista.map((s) => {
              const from = tr.mapping.map(s.from);
              const to = tr.mapping.map(s.to, -1);
              return { ...s, from, to, resolvida: s.resolvida ?? (to <= from ? 'obsoleta' : undefined) };
            });
            if (meta?.resolvidas) {
              const como = new Map(meta.resolvidas.map((r) => [r.id, r.como]));
              lista = lista.map((s) => (como.has(s.id) ? { ...s, resolvida: como.get(s.id) } : s));
            }
            return { lista, foco: meta?.foco !== undefined ? meta.foco : val.foco };
          },
        },
        props: {
          decorations: (state) => {
            const s = CHAVE_IA.getState(state);
            if (!s?.lista.length) return null;
            return DecorationSet.create(
              state.doc,
              s.lista
                .filter((x) => !x.resolvida && x.to > x.from)
                .map((x) => Decoration.inline(x.from, x.to, { class: x.id === s.foco ? 'cm-ef-sug cm-ef-sug-foco' : 'cm-ef-sug', 'data-sugestao': x.id })),
            );
          },
        },
      }),
    ];
  },
  addCommands() {
    return {
      definirSugestoes:
        (lista) =>
        ({ state, tr, dispatch }: CommandProps) => {
          // Cada sugestão é ancorada na primeira ocorrência ainda livre do trecho.
          const usadas: { from: number; to: number }[] = [];
          const ancoradas: SugestaoNoTexto[] = [];
          for (const s of lista) {
            const a = procurar(state.doc, s.de, true, false).find((x) => !usadas.some((u) => u.from < x.to && x.from < u.to));
            if (!a) continue;
            usadas.push(a);
            ancoradas.push({ ...s, ...a });
          }
          if (dispatch) dispatch(tr.setMeta(CHAVE_IA, { lista: ancoradas, foco: null }));
          return true;
        },
      resolverSugestao:
        (id, aceitar) =>
        ({ state, tr, dispatch }: CommandProps) => {
          const s = CHAVE_IA.getState(state)?.lista.find((x) => x.id === id);
          if (!s || s.resolvida) return false;
          if (aceitar && !aplicarSugestao(tr, s)) {
            // O trecho não é mais o que a IA leu: a sugestão caducou.
            if (dispatch) dispatch(tr.setMeta(CHAVE_IA, { resolvidas: [{ id, como: 'obsoleta' }] }));
            return false;
          }
          tr.setMeta(CHAVE_IA, { resolvidas: [{ id, como: aceitar ? 'aceita' : 'recusada' }] });
          // Sugestão aceita ou recusada é da IA: não conta como "a pessoa mexeu".
          tr.setMeta(ORIGEM_IA, true);
          if (dispatch) dispatch(tr);
          return true;
        },
      aceitarTodasSugestoes:
        () =>
        ({ state, tr, dispatch }: CommandProps) => {
          // Do fim para o começo: trocar um trecho não desloca os que vêm antes dele.
          const pendentes = (CHAVE_IA.getState(state)?.lista ?? []).filter((x) => !x.resolvida).sort((a, b) => b.from - a.from);
          if (!pendentes.length) return false;
          const resolvidas = pendentes.map((x) => ({ id: x.id, como: (aplicarSugestao(tr, x) ? 'aceita' : 'obsoleta') as Resolucao }));
          tr.setMeta(CHAVE_IA, { resolvidas, foco: null });
          tr.setMeta(ORIGEM_IA, true);
          if (dispatch) dispatch(tr);
          return true;
        },
      focarSugestao:
        (id) =>
        ({ tr, dispatch }: CommandProps) => {
          // Só o foco (o balão abre nele). Sem mexer na seleção: quem clicou no trecho
          // para corrigi-lo à mão perderia o cursor; a rolagem é de quem navega.
          tr.setMeta(CHAVE_IA, { foco: id });
          if (dispatch) dispatch(tr);
          return true;
        },
    };
  },
});

export const sugestoesNoEstado = (state: EditorState) => CHAVE_IA.getState(state)?.lista ?? [];
export const buscaNoEstado = (state: EditorState) => CHAVE_BUSCA.getState(state);
